import React from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  formatCountryCodesToString,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { TripMatch } from '@marageti/z4-sdk/lib/travel/TripMatch';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { Review } from '@marageti/z4-sdk/lib/travel';
import StarIcon from '@mui/icons-material/Star';

const TableSoldTrips = ({ tripMatches, reviews }: { tripMatches: TripMatch[] | undefined, reviews: Review[] | undefined, }) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);

  if (!tripMatches || tripMatches.length === 0) {
    return (
      <Stack spacing={1.5} my={6} display="flex" alignItems="center" justifyContent="center">
        <OutlinedIcons.OutlinedExclamationCircleIcon color="disabled" sx={{ width: 48, height: 48 }} />
        <Typography variant="body" color="text.secondary" sx={{ textAlign: 'center' }}>
          There are no sold trips for this traveler
        </Typography>
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 450, mt: 1, mb: 3 }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Destination</TableCell>
            <TableCell sx={{ minWidth: '120px' }}>Departure</TableCell>
            <TableCell>Partner</TableCell>
            <TableCell>Trip Price</TableCell>
            <TableCell>PPPD</TableCell>
            <TableCell sx={{ minWidth: '80px' }}>Review</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tripMatches.map((tripMatch) => {
            const matchingReview = reviews?.find((review) => review.tripMatchId === tripMatch.id);
            const rating = matchingReview ? (matchingReview.rating / 20) : 0;

            return (
              <TableRow
                key={tripMatch.referenceId}
                onClick={() => window.open(`/trip-matches/${tripMatch.id}`, '_blank')}
                sx={{
                  textDecoration: 'none',
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                }}
                hover
              >
                <TableCell component="th" scope="row">
                  {countryCodes ? formatCountryCodesToString(tripMatch.countries, countryCodes) : '-'}
                </TableCell>
                <TableCell sx={{ minWidth: '120px' }}>{getFormattedDateShortMonthDDYYYY(tripMatch.departureDate) || '-'}</TableCell>
                <TableCell>{tripMatch.partnerName || '-'}</TableCell>
                <TableCell>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tripMatch.totalTripPrice.amountUSD) || '-'}</TableCell>
                <TableCell>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tripMatch.totalPppd) || '-'}</TableCell>
                <TableCell
                  sx={{
                    verticalAlign: 'middle',
                    minWidth: '80px',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center">
                    {rating > 0 ? (
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(`/reviews/${matchingReview?.id}`, '_blank');
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <StarIcon
                          color="warning"
                          sx={{ width: 20, height: 20, mr: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {rating.toFixed(1)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" textAlign="center">-</Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableSoldTrips;
